/**
 * FamilyPlatform Universal Icon & Button Explanatory Tooltip Engine
 * High-performance, zero-dependency, accessible tooltip system.
 * Complies with Global Design Standards v2.1 and WCAG AA accessibility.
 *
 * Enforces British English (en-GB) explanatory tooltips with:
 * 1. Intent Dwell Delay (450ms hesitation check - zero popups on swift clicks)
 * 2. 3-Strike Decay & 1-Click Mastery (silently graduates familiar users)
 * 3. User Preference Mode: 'smart' (default), 'always', or 'off'
 * 4. Keyboard accessibility and screen-reader compliance preserved across all modes
 */
(function(window, document) {
    'use strict';

    if (window.__FamilyTooltipEngineInitialized) return;
    window.__FamilyTooltipEngineInitialized = true;

    // Comprehensive semantic dictionary mapping icon classes to en-GB explanations
    var ICON_DICTIONARY = [
        // Navigation & Core
        { match: /(^|\s)(bi-house|fa-home)($|\s)/, text: 'Go to main family feed' },
        { match: /(^|\s)(bi-diagram-3|fa-tree|fa-sitemap)($|\s)/, text: 'Explore living family tree and lineage' },
        { match: /(^|\s)(bi-stars|bi-star)($|\s)/, text: 'Family Studio and celebrations' },
        { match: /(^|\s)(bi-journal|fa-book|bi-book)($|\s)/, text: 'Family journal and memoirs' },
        { match: /(^|\s)(bi-camera-reels|fa-film)($|\s)/, text: 'Family reels and short videos' },
        { match: /(^|\s)(bi-people|fa-users|fa-user-friends)($|\s)/, text: 'Browse family members directory' },
        { match: /(^|\s)(bi-person-plus|fa-user-plus)($|\s)/, text: 'Invite family relatives and friends' },
        { match: /(^|\s)(bi-person|fa-user)($|\s)/, text: 'View profile page' },
        { match: /(^|\s)(bi-bell)($|\s)/, text: 'View alerts and family notifications' },
        { match: /(^|\s)(bi-gear|fa-cog|fa-gear)($|\s)/, text: 'Manage account settings and privacy' },
        { match: /(^|\s)(bi-search|fa-search)($|\s)/, text: 'Search family memories, activities, and records' },
        { match: /(^|\s)(bi-grid)($|\s)/, text: 'Open Family Hub and apps' },

        // Post & Composer Tools
        { match: /(^|\s)(bi-camera-fill|bi-camera|fa-camera)($|\s)/, text: 'Take or upload photos' },
        { match: /(^|\s)(bi-image-fill|bi-image|fa-image)($|\s)/, text: 'Upload photos to family memory' },
        { match: /(^|\s)(bi-play-btn|bi-camera-video|fa-video|fa-play)($|\s)/, text: 'Embed or upload family video' },
        { match: /(^|\s)(bi-megaphone|fa-bullhorn)($|\s)/, text: 'Broadcast important family announcement' },
        { match: /(^|\s)(bi-bar-chart|fa-poll|fa-chart-bar)($|\s)/, text: 'Create an interactive family poll' },
        { match: /(^|\s)(bi-hourglass|fa-history)($|\s)/, text: 'Then & Now photo comparison' },
        { match: /(^|\s)(bi-filetype-gif)($|\s)/, text: 'Attach animated family GIF' },
        { match: /(^|\s)(bi-emoji-smile|fa-smile)($|\s)/, text: 'Add emoji reaction' },
        { match: /(^|\s)(bi-tag|fa-tag)($|\s)/, text: 'Tag family relatives in this post' },
        { match: /(^|\s)(bi-mic|fa-microphone)($|\s)/, text: 'Record voice note or audio capsule' },
        { match: /(^|\s)(bi-apple)($|\s)/, text: 'Share directly from iPhone Apple Photos' },
        { match: /(^|\s)(bi-send)($|\s)/, text: 'Send comment or message' },

        // Feed Post Actions
        { match: /(^|\s)(bi-hand-thumbs-up|fa-thumbs-up)($|\s)/, text: 'React with appreciation' },
        { match: /(^|\s)(bi-chat|bi-chat-bubble|fa-comment)($|\s)/, text: 'Write or view comments' },
        { match: /(^|\s)(bi-share|fa-share)($|\s)/, text: 'Share memory with family relatives' },
        { match: /(^|\s)(bi-three-dots|bi-three-dots-vertical|fa-ellipsis)($|\s)/, text: 'More options and actions' },
        { match: /(^|\s)(bi-pencil|fa-pencil|fa-edit)($|\s)/, text: 'Edit details' },
        { match: /(^|\s)(bi-trash|fa-trash)($|\s)/, text: 'Delete item permanently' },
        { match: /(^|\s)(bi-eye|fa-eye)($|\s)/, text: 'View family engagement count' },
        { match: /(^|\s)(bi-bookmark|fa-bookmark)($|\s)/, text: 'Save memory to dynasty archive' },
        { match: /(^|\s)(bi-download|fa-download)($|\s)/, text: 'Download file or shortcut' },
        { match: /(^|\s)(bi-clipboard|fa-copy)($|\s)/, text: 'Copy to clipboard' },
        { match: /(^|\s)(bi-arrows-angle-expand|fa-expand)($|\s)/, text: 'Enlarge photo' },

        // Navigation Controls
        { match: /(^|\s)(bi-arrow-bar-up|bi-arrow-up|fa-arrow-up)($|\s)/, text: 'Back to top of page' },
        { match: /(^|\s)(bi-arrow-left|fa-arrow-left)($|\s)/, text: 'Go back to previous page' },
        { match: /(^|\s)(bi-arrow-right|fa-arrow-right)($|\s)/, text: 'Proceed to next step' },
        { match: /(^|\s)(bi-chevron-right|fa-chevron-right)($|\s)/, text: 'View more details' },
        { match: /(^|\s)(bi-chevron-left|fa-chevron-left)($|\s)/, text: 'Go back' },
        { match: /(^|\s)(bi-chevron-up|fa-chevron-up)($|\s)/, text: 'Collapse section' },
        { match: /(^|\s)(bi-chevron-down|fa-chevron-down)($|\s)/, text: 'Expand section' },
        { match: /(^|\s)(bi-x-lg|bi-x|fa-times)($|\s)/, text: 'Close or dismiss' },
        { match: /(^|\s)(bi-plus-lg|bi-plus|fa-plus)($|\s)/, text: 'Create new item' },
        { match: /(^|\s)(bi-check-lg|bi-check|fa-check)($|\s)/, text: 'Approve or confirm' },

        // Security, Time & Settings
        { match: /(^|\s)(bi-shield-check|fa-shield)($|\s)/, text: 'Verified and end-to-end encrypted' },
        { match: /(^|\s)(bi-globe|fa-globe)($|\s)/, text: 'Language and regional timezone settings' },
        { match: /(^|\s)(bi-moon-stars|bi-moon|fa-moon)($|\s)/, text: 'Toggle dark or light theme mode' },
        { match: /(^|\s)(bi-box-arrow-right|fa-sign-out)($|\s)/, text: 'Sign out of your account' },
        { match: /(^|\s)(bi-calendar|fa-calendar)($|\s)/, text: 'View calendar and upcoming events' },
        { match: /(^|\s)(bi-ticket|fa-ticket)($|\s)/, text: 'RSVP to family event' },
        { match: /(^|\s)(bi-envelope|fa-envelope)($|\s)/, text: 'Send private family message' }
    ];

    var DWELL_DELAY_MS = 450; // Hesitation check delay
    var MAX_VIEWS_DECAY = 3;  // Maximum exposures before graduating power users
    var AUTO_DISMISS_DELAY_MS = 3000; // Tooltip auto-disappears after a few seconds

    var tooltipEl = null;
    var tooltipContentEl = null;
    var tooltipArrowEl = null;
    var activeTarget = null;
    var dwellTimer = null;
    var hideTimer = null;
    var touchTimer = null;
    var autoDismissTimer = null;

    /**
     * Storage Helpers for Frequency & Mastery Management
     */
    function getMode() {
        try {
            return localStorage.getItem('fam_tt_mode') || 'smart';
        } catch (e) {
            return 'smart';
        }
    }

    function setMode(mode) {
        try {
            if (['smart', 'always', 'off'].indexOf(mode) !== -1) {
                localStorage.setItem('fam_tt_mode', mode);
                window.dispatchEvent(new CustomEvent('family:tooltip-mode-changed', { detail: { mode: mode } }));
            }
        } catch (e) {}
    }

    function generateSlug(text, el) {
        if (!text && !el) return 'generic';
        var base = (el && el.id) ? el.id : text;
        return base.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 36);
    }

    function isMastered(slug) {
        try {
            return localStorage.getItem('fam_tt_m_' + slug) === '1';
        } catch (e) {
            return false;
        }
    }

    function markMastered(slug) {
        try {
            localStorage.setItem('fam_tt_m_' + slug, '1');
        } catch (e) {}
    }

    function getViewCount(slug) {
        try {
            return parseInt(localStorage.getItem('fam_tt_v_' + slug) || '0', 10);
        } catch (e) {
            return 0;
        }
    }

    function recordView(slug) {
        try {
            var current = getViewCount(slug);
            localStorage.setItem('fam_tt_v_' + slug, String(current + 1));
        } catch (e) {}
    }

    function resetMastery() {
        try {
            var keys = [];
            for (var i = 0; i < localStorage.length; i++) {
                var k = localStorage.key(i);
                if (k && (k.indexOf('fam_tt_m_') === 0 || k.indexOf('fam_tt_v_') === 0)) {
                    keys.push(k);
                }
            }
            keys.forEach(function(k) { localStorage.removeItem(k); });
        } catch (e) {}
    }

    function createTooltipDOM() {
        if (tooltipEl) return;
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'family-tooltip-container';
        tooltipEl.id = 'familyGlobalTooltip';
        tooltipEl.setAttribute('role', 'tooltip');
        tooltipEl.setAttribute('aria-hidden', 'true');

        tooltipContentEl = document.createElement('div');
        tooltipContentEl.className = 'family-tooltip-content';

        tooltipArrowEl = document.createElement('div');
        tooltipArrowEl.className = 'family-tooltip-arrow';

        tooltipEl.appendChild(tooltipContentEl);
        tooltipEl.appendChild(tooltipArrowEl);
        document.body.appendChild(tooltipEl);
    }

    /**
     * Resolves the best explanatory tooltip text for a target element.
     */
    function resolveTooltipText(el) {
        if (!el || !(el instanceof HTMLElement)) return null;

        // 1. Explicit data-tooltip
        var dt = el.getAttribute('data-tooltip') || el.dataset.tooltip;
        if (dt && dt.trim()) return dt.trim();

        // 2. Explicit title attribute
        var title = el.getAttribute('title');
        if (title && title.trim()) {
            el.setAttribute('data-tooltip', title.trim());
            el.removeAttribute('title');
            return title.trim();
        }

        // 3. Explicit aria-label if element is an icon or button
        var ariaLabel = el.getAttribute('aria-label');
        if (ariaLabel && ariaLabel.trim() && ariaLabel.trim().length > 2) {
            return ariaLabel.trim();
        }

        // 4. If target is a button or clickable link, check inner text
        var isButton = el.tagName === 'BUTTON' || 
                       el.getAttribute('role') === 'button' || 
                       el.classList.contains('btn') || 
                       (el.tagName === 'A' && (el.classList.contains('btn') || el.classList.contains('pwa-tab-item')));

        if (isButton) {
            var clone = el.cloneNode(true);
            var removeSelectors = ['i', 'svg', '.badge', '.spinner-border', '.pwa-tab-badge', '.notif-header-badge'];
            removeSelectors.forEach(function(sel) {
                var items = clone.querySelectorAll(sel);
                items.forEach(function(i) { i.remove(); });
            });
            var cleanText = clone.textContent.replace(/\s+/g, ' ').trim();
            if (cleanText && cleanText.length >= 2 && cleanText.length <= 40) {
                return cleanText;
            }
        }

        // 5. Check Icon Dictionary against class names
        var iconEl = null;
        if (el.matches('i, svg, [class*="icon"], [class*="bi-"], [class*="fa-"]')) {
            iconEl = el;
        } else {
            iconEl = el.querySelector('i, svg, [class*="icon"], [class*="bi-"], [class*="fa-"]');
        }

        if (iconEl) {
            var className = iconEl.className || '';
            if (typeof className !== 'string') {
                className = iconEl.getAttribute('class') || '';
            }

            for (var i = 0; i < ICON_DICTIONARY.length; i++) {
                if (ICON_DICTIONARY[i].match.test(className)) {
                    return ICON_DICTIONARY[i].text;
                }
            }
        }

        // 6. Check common ID or action hints
        var id = el.id || '';
        if (/search/i.test(id)) return 'Search family activities and records';
        if (/darkmode/i.test(id) || /theme/i.test(id)) return 'Toggle dark or light theme mode';
        if (/notification/i.test(id)) return 'View alerts and family notifications';
        if (/camera|photo/i.test(id)) return 'Take or upload photos';
        if (/backtotop/i.test(id)) return 'Back to top of page';

        return null;
    }

    /**
     * Finds the nearest eligible tooltip host for an event target.
     */
    function findTooltipHost(target) {
        if (!target || !(target instanceof HTMLElement)) return null;

        var curr = target;
        var depth = 0;
        while (curr && curr !== document.body && depth < 5) {
            if (curr.getAttribute('data-tooltip') || curr.getAttribute('title')) {
                return curr;
            }
            if (curr.matches('i.bi, i[class*="fa-"], i.fas, i.far, i.fa, svg.bi, svg[class*="icon"], .icon-btn, .avatar-zoom-cue, .avatar-camera-btn')) {
                return curr;
            }
            if (curr.matches('button, a.btn, [role="button"], .btn, .pwa-tab-item, .composer-icon-btn, .btn-composer-action, .composer-tool, .gallery-back-btn, .lightbox-nav')) {
                return curr;
            }
            curr = curr.parentElement;
            depth++;
        }
        return null;
    }

    /**
     * Positions and reveals the tooltip near the target element.
     */
    function showTooltip(el, isImmediate) {
        if (!el || !document.body.contains(el)) return;

        var text = resolveTooltipText(el);
        if (!text) return;

        var slug = generateSlug(text, el);

        // Always sync aria-label for accessibility (independent of mode or decay)
        if (!el.getAttribute('aria-label') && (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button')) {
            el.setAttribute('aria-label', text);
        }

        var mode = getMode();

        // If mode is 'off' and this is a mouse event, suppress visual tooltip
        if (mode === 'off' && !isImmediate) return;

        // In 'smart' mode: check mastery & exposure decay on hover
        if (mode === 'smart' && !isImmediate) {
            if (isMastered(slug) || getViewCount(slug) >= MAX_VIEWS_DECAY) {
                return; // User has graduated; do not display tooltip on hover
            }
        }

        createTooltipDOM();
        clearTimeout(hideTimer);
        clearTimeout(autoDismissTimer);

        // Security invariant: strictly textContent to banish DOM XSS
        tooltipContentEl.textContent = text;
        activeTarget = el;

        // Record exposure in smart mode
        if (mode === 'smart') {
            recordView(slug);
        }

        // Render hidden first to calculate exact dimensions
        tooltipEl.style.display = 'block';
        tooltipEl.style.opacity = '0';
        tooltipEl.style.transform = 'translate3d(0, 0, 0)';

        var targetRect = el.getBoundingClientRect();
        var tipRect = tooltipEl.getBoundingClientRect();

        var viewportWidth = window.innerWidth;
        var viewportHeight = window.innerHeight;

        // Preferred: Top of element
        var top = targetRect.top - tipRect.height - 8;
        var placeOnBottom = false;

        if (top < 10) {
            top = targetRect.bottom + 8;
            placeOnBottom = true;
        }

        // Center horizontally
        var targetCenter = targetRect.left + (targetRect.width / 2);
        var left = targetCenter - (tipRect.width / 2);

        // Clamp inside viewport
        var padding = 12;
        if (left < padding) left = padding;
        if (left + tipRect.width > viewportWidth - padding) {
            left = viewportWidth - tipRect.width - padding;
        }

        // Position arrow
        var arrowLeft = targetCenter - left;
        arrowLeft = Math.max(12, Math.min(arrowLeft, tipRect.width - 12));

        tooltipEl.style.top = Math.round(top) + 'px';
        tooltipEl.style.left = Math.round(left) + 'px';

        if (placeOnBottom) {
            tooltipEl.setAttribute('data-placement', 'bottom');
            tooltipArrowEl.style.top = '-5px';
            tooltipArrowEl.style.bottom = 'auto';
            tooltipArrowEl.style.left = Math.round(arrowLeft) + 'px';
        } else {
            tooltipEl.setAttribute('data-placement', 'top');
            tooltipArrowEl.style.bottom = '-5px';
            tooltipArrowEl.style.top = 'auto';
            tooltipArrowEl.style.left = Math.round(arrowLeft) + 'px';
        }

        requestAnimationFrame(function() {
            tooltipEl.style.opacity = '1';
            tooltipEl.setAttribute('aria-hidden', 'false');
        });

        // Auto-dismiss invariant: Tooltips disappear automatically after a few seconds
        clearTimeout(autoDismissTimer);
        autoDismissTimer = setTimeout(function() {
            hideTooltip();
        }, AUTO_DISMISS_DELAY_MS);
    }

    /**
     * Hides the active tooltip with smooth fade-out.
     */
    function hideTooltip() {
        clearTimeout(dwellTimer);
        clearTimeout(touchTimer);
        clearTimeout(autoDismissTimer);
        if (!tooltipEl) return;
        clearTimeout(hideTimer);

        tooltipEl.style.opacity = '0';
        tooltipEl.setAttribute('aria-hidden', 'true');

        hideTimer = setTimeout(function() {
            if (tooltipEl && tooltipEl.style.opacity === '0') {
                tooltipEl.style.display = 'none';
                activeTarget = null;
            }
        }, 150);
    }

    // Attach passive listeners for mouse and keyboard navigation
    document.addEventListener('mouseover', function(e) {
        var host = findTooltipHost(e.target);
        if (host && host !== activeTarget) {
            clearTimeout(dwellTimer);
            var mode = getMode();
            var delay = (mode === 'always') ? 150 : DWELL_DELAY_MS;

            // Intent Dwell Timer: hesitation check
            dwellTimer = setTimeout(function() {
                showTooltip(host, false);
            }, delay);
        }
    }, { passive: true });

    document.addEventListener('mouseout', function(e) {
        clearTimeout(dwellTimer);
        if (activeTarget && (!e.relatedTarget || !activeTarget.contains(e.relatedTarget))) {
            hideTooltip();
        }
    }, { passive: true });

    // 1-Click Mastery: When an icon/button is clicked, mark it mastered immediately
    document.addEventListener('click', function(e) {
        clearTimeout(dwellTimer);
        clearTimeout(autoDismissTimer);
        hideTooltip();
        var host = findTooltipHost(e.target);
        if (host) {
            var text = resolveTooltipText(host);
            if (text) {
                var slug = generateSlug(text, host);
                markMastered(slug);
            }
        }
    }, { passive: true });

    // Keyboard navigation: immediate display on focus (Accessibility Invariant)
    document.addEventListener('focusin', function(e) {
        clearTimeout(dwellTimer);
        var host = findTooltipHost(e.target);
        if (host) {
            showTooltip(host, true);
        }
    }, { passive: true });

    document.addEventListener('focusout', function(e) {
        hideTooltip();
    }, { passive: true });

    // Mobile touch & tap support: quick reveal on tap with auto-dismiss
    document.addEventListener('touchstart', function(e) {
        clearTimeout(dwellTimer);
        var host = findTooltipHost(e.target);
        if (host) {
            showTooltip(host, true);
        } else {
            hideTooltip();
        }
    }, { passive: true });

    // Dismiss immediately when opening dropdowns or modals to prevent hovering over menus
    document.addEventListener('show.bs.dropdown', function() {
        hideTooltip();
    }, { passive: true });
    document.addEventListener('show.bs.modal', function() {
        hideTooltip();
    }, { passive: true });

    // Defensive Mobile & Desktop Viewport Boundary Clamping for Dropdown Menus
    document.addEventListener('shown.bs.dropdown', function(event) {
        var toggle = event.target;
        var parent = toggle && toggle.closest ? toggle.closest('.dropdown') : null;
        var menu = parent ? parent.querySelector('.dropdown-menu') : null;
        if (!menu) {
            menu = (toggle.nextElementSibling && toggle.nextElementSibling.classList && toggle.nextElementSibling.classList.contains('dropdown-menu')) 
                ? toggle.nextElementSibling 
                : null;
        }
        if (!menu) return;

        var rect = menu.getBoundingClientRect();
        var vw = window.innerWidth;
        var padding = 12;

        if (rect.left < padding) {
            var offset = Math.round(padding - rect.left);
            menu.style.setProperty('transform', 'translateX(' + offset + 'px)', 'important');
        } else if (rect.right > (vw - padding)) {
            var offset = Math.round(rect.right - (vw - padding));
            menu.style.setProperty('transform', 'translateX(-' + offset + 'px)', 'important');
        }
    }, { passive: true });

    document.addEventListener('hidden.bs.dropdown', function(event) {
        var toggle = event.target;
        var parent = toggle && toggle.closest ? toggle.closest('.dropdown') : null;
        var menu = parent ? parent.querySelector('.dropdown-menu') : null;
        if (menu) {
            menu.style.removeProperty('transform');
        }
    }, { passive: true });

    // Dismiss on scroll or escape key
    window.addEventListener('scroll', function() {
        clearTimeout(dwellTimer);
        if (activeTarget) hideTooltip();
    }, { passive: true });

    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
            clearTimeout(dwellTimer);
            hideTooltip();
        }
    });

    // Auto-discover existing icons and cache titles to prevent native browser tooltip collisions
    function scanAndEnhanceTooltips(root) {
        var context = root || document;
        var elements = context.querySelectorAll('[title], [data-tooltip], button, a.btn, [role="button"], i.bi, i[class*="fa-"]');
        for (var i = 0; i < elements.length; i++) {
            var el = elements[i];
            var title = el.getAttribute('title');
            if (title && title.trim()) {
                el.setAttribute('data-tooltip', title.trim());
            }
        }
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            createTooltipDOM();
            scanAndEnhanceTooltips();
        });
    } else {
        createTooltipDOM();
        scanAndEnhanceTooltips();
    }

    // Dynamic MutationObserver to monitor newly inserted feed items, comments, modals
    if (window.MutationObserver) {
        var observer = new MutationObserver(function(mutations) {
            for (var m = 0; m < mutations.length; m++) {
                if (mutations[m].addedNodes.length > 0) {
                    for (var n = 0; n < mutations[m].addedNodes.length; n++) {
                        var node = mutations[m].addedNodes[n];
                        if (node.nodeType === 1) { // ELEMENT_NODE
                            scanAndEnhanceTooltips(node);
                        }
                    }
                }
            }
        });

        observer.observe(document.documentElement, {
            childList: true,
            subtree: true
        });
    }

    // Expose programmatic helper API
    window.FamilyTooltip = {
        show: showTooltip,
        hide: hideTooltip,
        scan: scanAndEnhanceTooltips,
        resolve: resolveTooltipText,
        getMode: getMode,
        setMode: setMode,
        isMastered: isMastered,
        markMastered: markMastered,
        getViewCount: getViewCount,
        resetMastery: resetMastery,
        generateSlug: generateSlug
    };

})(window, document);
